/**
 * Stable identity for a membership, independent of its position in the
 * memberships array.
 *
 * The active context is tracked as an *index* into `user.memberships`. When
 * that array is rebuilt from the server (Layout's /session/contexts resync),
 * the server may return memberships in a different order — so the old index
 * can point at a different membership, silently switching the user's active
 * role/location (and the X-Active-Context header sent on every request).
 *
 * `remapActiveIndex` recomputes the index so it keeps pointing at the SAME
 * membership after a reorder. Key = role|locationId|organizationId, which is
 * exactly what the backend's context header is built from, so it uniquely
 * identifies a switchable context.
 */
export interface MembershipIdentity {
  role?: string;
  locationId?: string;
  organizationId?: string;
}

export function membershipKey(m: MembershipIdentity): string {
  return `${m.role || ''}|${m.locationId || ''}|${m.organizationId || ''}`;
}

/**
 * Given the old membership array, the new (reordered/changed) one, and the old
 * active index, return the index in the new array pointing at the same
 * membership. Falls back to 0 when the previously-active membership no longer
 * exists (e.g. it was revoked), so the user lands on a valid context rather
 * than a stale or out-of-range one.
 */
export function remapActiveIndex(
  oldMemberships: MembershipIdentity[],
  newMemberships: MembershipIdentity[],
  oldIndex: number
): number {
  const active = oldMemberships[oldIndex];
  if (!active) return 0;
  const key = membershipKey(active);
  const next = newMemberships.findIndex((m) => membershipKey(m) === key);
  return next >= 0 ? next : 0;
}
